import { z } from "zod"

export const propertyTransactionSchema = z.enum(["buy", "rent"])
export const propertyTypeSchema = z.enum([
  "residential",
  "commercial",
  "land",
  "investment",
  "holiday-rental",
])

const optionalNumber = z.number().finite().nonnegative().optional()

export const propertySearchSchema = z.object({
  transaction: propertyTransactionSchema.optional(),
  type: z.union([propertyTypeSchema, z.literal("all")]).optional(),
  keywords: z.string().trim().max(120).optional(),
  reference: z.string().trim().max(30).optional(),
  region: z.string().trim().max(80).optional(),
  locality: z.string().trim().max(80).optional(),
  minPrice: optionalNumber,
  maxPrice: optionalNumber,
  bedrooms: z.number().int().min(0).max(20).optional(),
  bathrooms: z.number().int().min(0).max(20).optional(),
  minFloorArea: optionalNumber,
  minLandArea: optionalNumber,
  nearCbd: z.boolean().optional(),
  waterfront: z.boolean().optional(),
  sort: z.enum(["newest", "price-asc", "price-desc"]).optional(),
  page: z.number().int().min(1).max(1000).optional(),
  pageSize: z.number().int().min(1).max(48).optional(),
})

export const loginSchema = z.object({
  email: z.string().trim().email().max(255),
  password: z.string().min(8).max(100),
  remember: z.boolean().optional(),
})

export const registerSchema = z.object({
  name: z.string().trim().min(2).max(80),
  email: z.string().trim().email().max(255),
  phone: z.string().trim().min(7).max(30),
  password: z.string().min(10).max(100),
})

export const idSchema = z.object({
  id: z.number().int().positive(),
})

export const slugSchema = z.object({
  slug: z.string().trim().min(1).max(120),
})

export const saveSearchSchema = z.object({
  name: z.string().trim().min(2).max(60),
  filters: propertySearchSchema,
})

export const inquirySchema = z.object({
  propertyId: z.number().int().positive().nullable().optional(),
  name: z.string().trim().min(2).max(80),
  email: z.string().trim().email().max(255),
  phone: z.string().trim().max(30).default(""),
  message: z.string().trim().min(10).max(1500),
})

export const profileSchema = z.object({
  name: z.string().trim().min(2).max(80),
  phone: z.string().trim().min(7).max(30),
  locale: z.enum(["en", "fr", "zh"]),
  currentPassword: z.string().max(100).optional(),
  newPassword: z.string().min(10).max(100).optional(),
})

export const alertSchema = z.object({
  enabled: z.boolean(),
  frequency: z.enum(["daily", "weekly", "monthly"]),
  filters: propertySearchSchema,
})

export const propertyEditorSchema = z.object({
  id: z.number().int().positive().optional(),
  reference: z.string().trim().min(3).max(30),
  title: z.string().trim().min(4).max(120),
  description: z.string().trim().min(30).max(5000),
  transaction: propertyTransactionSchema,
  type: propertyTypeSchema,
  price: z.number().finite().nonnegative(),
  rentPeriod: z.string().trim().max(40).nullable().optional(),
  region: z.string().trim().min(2).max(80),
  locality: z.string().trim().min(2).max(80),
  address: z.string().trim().min(4).max(160),
  latitude: z.number().finite().min(-90).max(90),
  longitude: z.number().finite().min(-180).max(180),
  bedrooms: z.number().int().min(0).max(30),
  bathrooms: z.number().int().min(0).max(30),
  floorArea: z.number().finite().nonnegative(),
  landArea: z.number().finite().nonnegative(),
  nearCbd: z.boolean(),
  waterfront: z.boolean(),
  featured: z.boolean(),
  status: z.enum(["published", "draft", "archived"]),
  amenities: z.array(z.string().trim().min(1).max(60)).max(30),
  imageUrl: z
    .string()
    .trim()
    .refine(
      (value) =>
        value.startsWith("/images/properties/") || value.startsWith("https://"),
      "Use a demo media path or a secure image URL."
    ),
})

export const newsletterSchema = z.object({
  email: z.string().trim().email().max(255),
})

export const contentQuerySchema = z.object({
  kind: z
    .enum([
      "news",
      "event",
      "page",
      "faq",
      "team",
      "testimonial",
      "partner",
      "home",
    ])
    .optional(),
  slug: z.string().trim().max(120).optional(),
})

export const adminSectionSchema = z.object({
  section: z.enum([
    "properties",
    "inquiries",
    "users",
    "content",
    "taxonomies",
    "settings",
  ]),
})

export const adminActionSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("inquiry"),
    id: z.number().int().positive(),
    status: z.enum(["new", "read", "replied", "closed"]),
    response: z.string().trim().max(2000).default(""),
  }),
  z.object({
    kind: z.literal("user"),
    id: z.number().int().positive(),
    role: z.enum(["member", "admin"]),
    status: z.enum(["active", "disabled"]),
  }),
  z.object({
    kind: z.literal("content"),
    id: z.number().int().positive().optional(),
    contentKind: z.enum([
      "news",
      "event",
      "page",
      "faq",
      "team",
      "testimonial",
      "partner",
      "home",
    ]),
    slug: z.string().trim().min(2).max(120),
    title: z.string().trim().min(2).max(160),
    excerpt: z.string().trim().max(500),
    body: z.string().trim().min(4).max(8000),
    imageUrl: z.string().trim().max(500),
    startsAt: z.string().trim().max(40).nullable(),
    status: z.enum(["published", "draft"]),
    sortOrder: z.number().int().min(0).max(1000),
  }),
  z.object({
    kind: z.literal("taxonomy"),
    id: z.number().int().positive().optional(),
    taxonomyKind: z.enum(["attribute", "keyword", "property-type"]),
    name: z.string().trim().min(2).max(80),
  }),
  z.object({
    kind: z.literal("region"),
    id: z.number().int().positive().optional(),
    name: z.string().trim().min(2).max(80),
    country: z.string().trim().min(2).max(80),
  }),
  z.object({
    kind: z.literal("currency"),
    id: z.number().int().positive().optional(),
    code: z.string().trim().length(3).toUpperCase(),
    label: z.string().trim().min(2).max(80),
    symbol: z.string().trim().min(1).max(8),
    rate: z.number().positive(),
    isDefault: z.boolean(),
  }),
])

export type AdminActionInput = z.infer<typeof adminActionSchema>

export const adminDeleteSchema = z.object({
  kind: z.enum(["content", "taxonomy", "region", "currency", "subscriber"]),
  id: z.number().int().positive(),
})
